import { makeBenefitsApi } from 'src/http/api/internal/internal-http';
import { ThemesRepository } from 'src/repositories/benefits/themes.repository';
import {
  productsByThemeType,
  ThemeBulkDeleteResponse,
  ThemeDeletionVerificationResponse,
  ThemeStatus,
  themeType,
} from 'src/types/products';
import { create } from 'zustand';

type States = {
  loadingThemesList: boolean;
  loadingThemeDetails: boolean;
  loadingThemeCreate: boolean;
  loadingThemeUpdate: boolean;
  loadingThemesBulkDelete: boolean;
  loadingThemesDeletionVerification: boolean;
  isLoadingProductsByTheme: boolean;
  themesList: themeType[];
  themeDetails: themeType | null;
  productsByTheme: productsByThemeType | null;
};

type Actions = {
  getThemeDetails: (id: string) => Promise<void>;
  getThemesList: () => Promise<void>;
  resetThemeDetails: () => Promise<void>;
  createTheme: (theme: any) => Promise<void>;
  updateTheme: (theme: any) => Promise<void>;
  changeThemeStatus: (themeId: string, status: ThemeStatus) => Promise<void>;
  deleteThemes: (themeIds: string[]) => Promise<ThemeBulkDeleteResponse>;
  verifyThemesDeletion: (themeIds: string[]) => Promise<ThemeDeletionVerificationResponse>;
  getProductsByTheme: (id: string) => Promise<void>;
};

type Store = Actions & States;

const initialValues = {
  loadingThemesList: false,
  loadingThemeDetails: false,
  loadingThemeCreate: false,
  loadingThemeUpdate: false,
  loadingThemesBulkDelete: false,
  loadingThemesDeletionVerification: false,
  isLoadingProductsByTheme: false,
  themesList: [],
  themeDetails: null,
  productsByTheme: null,
};

const repository = new ThemesRepository(makeBenefitsApi());

export const useThemeStore = create<Store>((set) => ({
  ...initialValues,

  getThemesList: async () => {
    set({ loadingThemesList: true });
    const response = await repository.getThemesList();
    if (response.isLeft()) {
      set({ themesList: [], loadingThemesList: false });
      return;
    }
    set({
      themesList: response.value,
      loadingThemesList: false,
    });
  },

  getThemeDetails: async (id: string) => {
    set({ loadingThemeDetails: true });
    const response = await repository.getThemeDetails(id);
    if (response.isLeft()) {
      set({ themeDetails: null, loadingThemeDetails: false });
      return;
    }
    set({
      themeDetails: response.value,
      loadingThemeDetails: false,
    });
  },

  resetThemeDetails: async () => set({ themeDetails: null }),

  updateTheme: async (theme: any) => {
    set({ loadingThemeUpdate: true });
    const response = await repository.updateTheme(theme);
    if (response.isLeft()) {
      set({ loadingThemeUpdate: false });
      return;
    }
    set({
      loadingThemeUpdate: false,
    });
  },

  changeThemeStatus: async (themeId, status) => {
    const response = await repository.changeThemeStatus(themeId, status);

    if (response.isLeft()) {
      throw response.value;
    }

    set((state) => ({
      themesList: state.themesList.map((theme) =>
        theme._id === themeId ? { ...theme, status } : theme
      ),
    }));
  },

  deleteThemes: async (themeIds) => {
    set({ loadingThemesBulkDelete: true });

    try {
      const response = await repository.deleteThemes(themeIds);

      if (response.isLeft()) {
        throw response.value;
      }

      return response.value;
    } finally {
      set({ loadingThemesBulkDelete: false });
    }
  },

  verifyThemesDeletion: async (themeIds) => {
    set({ loadingThemesDeletionVerification: true });

    try {
      const response = await repository.verifyThemesDeletion(themeIds);

      if (response.isLeft()) {
        throw response.value;
      }

      return response.value;
    } finally {
      set({ loadingThemesDeletionVerification: false });
    }
  },

  createTheme: async (theme: any) => {
    set({ loadingThemeCreate: true });
    const response = await repository.createTheme(theme);
    if (response.isLeft()) {
      set({ loadingThemeCreate: false });
      return;
    }
    set({
      loadingThemeCreate: false,
    });
  },

  getProductsByTheme: async (id: string) => {
    set({ isLoadingProductsByTheme: true });
    const response = await repository.getProductsByTheme(id);
    if (response.isLeft()) {
      set({ productsByTheme: null, isLoadingProductsByTheme: false });
      return;
    }
    set({
      productsByTheme: response.value,
      isLoadingProductsByTheme: false,
    });
  },
}));
